/**
 * UserNotesBlock - Renders user's clinical notes with preserved formatting.
 * EVI-608: Fixes newlines not being respected in Evidence chat.
 *
 * The raw content is passed via data-raw attribute (base64 encoded) to preserve
 * newlines before Streamdown parses it as markdown. This component decodes the
 * raw text and uses TipTap to render it with the same styling as the scribe note.
 *
 * Includes action buttons at the bottom (left-aligned when session is linked):
 * - Create document: New document tab with content (first)
 * - Copy: Copy content to clipboard
 * - Add to Note: Append content to current note/document
 * - Replace Note: Replace current note/document with content
 *
 * @example LLM Output:
 * ```
 * <usernotes>
 * S: worried
 *
 * O: male 45, afebrile
 * </usernotes>
 * ```
 *
 * Gets pre-processed to:
 * <usernotes data-raw="base64encoded"></usernotes>
 *
 * Then this component decodes and renders with TipTap.
 */
import { Button } from '@heidi/design-system/button';
import { CopyButton } from '@heidi/design-system/copy-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useEditorContentSync } from '@heidi/editor/react';
import styles from '@/components/tiptap/shared/styles/index.module.scss';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { useHasPermissionToReplace } from '@/components/ui/ask-ai-input/hooks/useHasPermissionToReplace';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { cn } from '@/lib/cn';
import { toast } from '@heidi/design-system/toast';
import { useEvidenceCopyConfirmationGate } from '@/hooks/useEvidenceCopyConfirmationGate';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@heidi/runtime/i18n';
import { EditorContent } from '@tiptap/react';
import { FilePlus, ListPlus, PencilLine, Replace } from 'lucide-react';
import { useMemo, useCallback, useRef } from 'react';
import { useChatResponseActions } from '../hooks/useChatResponseActions';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import type { ChatMode } from '../types/types';

interface UserNotesActionButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}

/** Reusable icon button with tooltip used for the note-mutating actions. */
const ActionButton = ({ icon: Icon, label, onClick }: UserNotesActionButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={onClick}
        className="text-text-secondary hover:text-text-primary"
        aria-label={label}
      >
        <Icon />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top">{label}</TooltipContent>
  </Tooltip>
);

interface UserNotesBlockProps {
  /** Base64-encoded raw content (preserves \n characters) */
  rawContent?: string;
  /** Session ID for insert actions */
  sessionId?: string | null;
  /** Chat mode for analytics */
  mode?: ChatMode;
  /** Chat title for document naming when creating a new tab from draft */
  chatTitle?: string;
  /**
   * Chat ID used to scope the Evidence copy PHI/PII confirmation popover so the
   * attestation is asked once per chat (EVI-1452 / SWR-88). When omitted the
   * popover is skipped and copy runs immediately.
   */
  chatId?: string | null;
  /**
   * Backend message ID (msg_*) of the assistant response that owns this draft —
   * joins copy adoption (EVI-2101) to the producing model.
   */
  messageId?: string | null;
  className?: string;
}

/**
 * Decode base64 raw content back to original text with newlines.
 * Trims leading/trailing whitespace to avoid extra blank lines.
 */
const decodeRawContent = (encoded: string): string => {
  try {
    return decodeURIComponent(atob(encoded)).trim();
  } catch {
    // If decoding fails, return empty string
    return '';
  }
};

/**
 * UserNotesBlock - A styled container for displaying user's notes in Evidence chat.
 *
 * Uses TipTap editor (read-only) with the same styling as the scribe note editor.
 * Raw content is decoded from base64 to preserve newlines.
 * Includes Create document (when linked to a session), Copy, Add to Note, and Replace Note.
 */
export const UserNotesBlock = ({
  rawContent,
  sessionId,
  mode,
  chatTitle,
  chatId,
  messageId,
  className,
}: UserNotesBlockProps): JSX.Element | null => {
  const { t } = useTranslations();
  const { trackCopied } = useEvidenceAnalytics();

  // Anchor for the Evidence copy PHI/PII confirmation popover (EVI-1452 / SWR-88).
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const { doCopyWithConfirmation } = useEvidenceCopyConfirmationGate({
    chatId,
    messageId,
    surface: 'user_notes',
    anchorRef: copyButtonRef,
  });

  // Decode raw content for both display and actions
  const decodedText = useMemo(() => {
    if (!rawContent) return '';
    return decodeRawContent(rawContent);
  }, [rawContent]);

  // Convert to HTML for TipTap display
  const htmlContent = useMemo(() => {
    if (!decodedText) return '';
    // Collapse excessive blank lines (3+ consecutive newlines → double newline) to prevent
    // simpleMarkdownToHtml from generating empty <p> tags. Preserves intentional paragraph
    // breaks (\n\n) while eliminating runs of blank lines that TipTap renders as
    // full-height empty paragraphs.
    const normalizedText = decodedText.replace(/\n{3,}/g, '\n\n');
    return simpleMarkdownToHtml({ markdown: normalizedText });
  }, [decodedText]);

  // Use the same TipTap editor as scribe note, but read-only
  const { editor } = useBlockEditorMarkdown({
    content: htmlContent,
    editable: false,
    placeholder: '',
  });

  // Sync content when rawContent changes (handles streaming)
  useEditorContentSync({ editor, content: htmlContent, editorVariant: 'block_markdown' });

  // Get actions for this specific content (the decoded notes text)
  const { onAddToNote, onReplaceNote, onAddNewTab } = useChatResponseActions(
    decodedText,
    mode,
    sessionId,
    chatTitle,
    chatId
  );

  // EVI: Note-mutating actions (Create document / Add to note / Replace note)
  // must be hidden when the user lacks edit access to the session — e.g. an
  // admin reviewing another clinician's session without session-sharing edit
  // rights. Mirrors the gating already applied by ActionsPopover.
  const { hasPermissionToReplace } = useHasPermissionToReplace();

  // Copy just the notes content — analytics only after a successful clipboard write.
  const runCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(decodedText);
    } catch (error) {
      logger.error('Failed to copy user notes to clipboard', {
        error,
        contentLength: decodedText?.length,
      });
      toast({
        tone: 'destructive',
        title: t('common.error'),
        description: t('chat.failedToCopy'),
      });
      return false;
    }
    try {
      trackCopied({
        surface: 'user_notes',
        chatId,
        messageId,
        sessionId,
        mode,
      });
    } catch (error) {
      logger.error('Failed to track evidence_copied', { error, surface: 'user_notes' });
    }
    // Success is confirmed by the CopyButton's own icon/tooltip swap, so a
    // tracker throw must not downgrade it to a failure.
    return true;
  }, [decodedText, t, trackCopied, chatId, messageId, sessionId, mode]);

  const handleCopy = useCallback(
    () => doCopyWithConfirmation(runCopy),
    [doCopyWithConfirmation, runCopy]
  );

  // Don't render if there's no content
  if (!htmlContent) {
    return null;
  }

  const hasSessionActions = Boolean(sessionId) && hasPermissionToReplace;

  return (
    <div
      className={cn(
        // Container styling - subtle gray/neutral to distinguish from AI content
        // Using rounded-lg (8px), py-3 (12px top/bottom), px-4 (16px left/right)
        'rounded-lg border border-border-secondary bg-surface-accent px-4 py-3',
        // Spacing from surrounding content
        'my-4 first:mt-0',
        className
      )}
    >
      {/* Header - small label */}
      <div className="mb-3 flex items-center gap-1.5">
        <PencilLine className="size-3 text-text-tertiary" />
        <span className="text-xs text-text-tertiary">{t('chat.draftNote')}</span>
      </div>

      {/* Content area - TipTap editor with ProseMirror styling */}
      <EditorContent
        editor={editor}
        className={cn(styles.ProseMirror, '[&_.ProseMirror]:outline-none')}
      />

      {/* Action buttons — left-aligned; Create document first when session actions exist */}
      <div className="mt-6 flex flex-wrap items-center justify-start gap-1">
        {hasSessionActions ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disableAutomaticSvgSizing
            onClick={onAddNewTab}
            className="gap-1.5 text-text-secondary hover:text-text-primary"
          >
            <FilePlus className="size-4 shrink-0" />
            {t('chat.createDocumentFromDraft')}
          </Button>
        ) : null}
        <CopyButton
          ref={copyButtonRef}
          onCopy={handleCopy}
          variant="ghost"
          size="sm"
          className="text-text-secondary"
          label={t('tooltips.copy')}
          copiedLabel={t('shared.Copied')}
          data-testid="reply-action-copy"
        />
        {hasSessionActions ? (
          <>
            <ActionButton icon={ListPlus} label={t('chat.addToNote')} onClick={onAddToNote} />
            <ActionButton icon={Replace} label={t('chat.replaceNote')} onClick={onReplaceNote} />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UserNotesBlock;
